import { useFormik } from "formik";
import * as Yup from "yup";
import CrossIcon from "../../assests/images/dashboard/Vector 3.png";
import { useAuth } from "src/contexts/auth-provider";
import { useEffect, useState } from "react";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { UPDATE_RESTAURANT } from "src/constants/mutations";
import { successToast } from "src/utils/toasts";
import { BtnLoader } from "src/Loader";
import { convertCentsToDollars, convertDollarToCents } from "src/helpers";

const DeliveryPolicy = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant, sethasRestaurant } = useAuth();

  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      deliveryPolicies: [
        {
          startMile: "",
          endMile: "",
          deliveryChargeCents: "", // Changed from deliveryCharge to deliveryChargeCents
        },
      ],
    },
    validationSchema: Yup.object({
      deliveryPolicies: Yup.array()
          .of(
              Yup.object({
                startMile: Yup.number()
                    .required("Start Mile is required")
                    .min(0, "Start Mile must be greater than or equal to 0"),
                endMile: Yup.number()
                    .required("End Mile is required")
                    .min(0, "End Mile must be greater than or equal to 0"),
                deliveryChargeCents: Yup.string().required( // Changed validation field
                    "Delivery Charges is required"
                ),
              })
          )
          .test(
              "validate-mileage-order",
              "Start Mile must be equal to the End Mile of the previous policy",
              function (deliveryPolicies: any) {
                if (!deliveryPolicies || deliveryPolicies.length < 2) {
                  return true;
                }

                for (let i = 1; i < deliveryPolicies.length; i++) {
                  const prevPolicy = deliveryPolicies[i - 1];
                  const currentPolicy = deliveryPolicies[i];

                  const prevEndMile = parseInt(prevPolicy.endMile);
                  const currentStartMile = parseInt(currentPolicy.startMile);

                  if (currentStartMile !== prevEndMile) {
                    return false;
                  }
                }

                return true;
              }
          ),
    }),
    onSubmit: () => {},
  });

  useEffect(() => {
    if (hasRestaurant?.deliveryPolicies?.length > 0) {
      // Convert cents to dollars for display
      formik.setFieldValue("deliveryPolicies", hasRestaurant?.deliveryPolicies.map((item: any) => ({
        ...item,
        deliveryChargeCents: convertCentsToDollars(item.deliveryChargeCents) // Convert to dollars for display
      })));
    }
  }, [hasRestaurant?.deliveryPolicies]);

  const handleAddDeliveryPolicy = () => {
    const prevDeliveryPolicies = [...formik.values.deliveryPolicies];
    prevDeliveryPolicies.push({
      startMile: "",
      endMile: "",
      deliveryChargeCents: "", // Changed from deliveryCharge
    });
    formik.setFieldValue("deliveryPolicies", prevDeliveryPolicies);
  };

  const handleRemoveDeliveryPolicy = (index: any) => {
    const prevDeliveryPolicies = [...formik.values.deliveryPolicies];
    prevDeliveryPolicies.splice(index, 1);
    formik.setFieldValue("deliveryPolicies", prevDeliveryPolicies);
  };

  const updateRestaurantSetting = async () => {
    setLoading(true);
    try {
      const variables = {
        updateStoreInput: {
          storeCorrelationId: hasRestaurant.correlationId,
          deliveryPolicies: formik.values.deliveryPolicies.map(item => ({
            startMile: item.startMile,
            endMile: item.endMile,
            deliveryChargeCents: String(convertDollarToCents(item.deliveryChargeCents)) // Convert dollars back to cents
          }))
        },
      };

      const { data }: any = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_RESTAURANT,
        variables: variables,
      });

      const payloadData = {
        ...hasRestaurant,
        deliveryPolicies: variables.updateStoreInput.deliveryPolicies,
      };

      localStorage.setItem("restaurant", JSON.stringify(payloadData));
      sethasRestaurant(payloadData);
      setLoading(false);

      if (!data.errors) {
        successToast("Delivery Policy has been Updated!");
      }
    } catch (error) {
      console.error("Error updating delivery policy:", error);
      setLoading(false);
    }
  };

  const isDisabled = !formik.isValid || !formik.dirty;

  return (
    <div className="w-full max-w-[760px]">
      <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em]">Delivery policy</h2>
          <button
            type="button"
            className="text-[13px] font-medium text-[#6b7280] bg-transparent border-none cursor-pointer hover:text-[#111827] focus:outline-none"
            onClick={handleAddDeliveryPolicy}
          >
            Add
          </button>
        </div>
        <div className="space-y-3">
          {formik.values.deliveryPolicies?.map((policy: any, index: any) => (
            <div key={index} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2">
              <div className="flex items-center gap-x-2">
                <label className="text-[14px] font-medium text-[#4b5563] whitespace-nowrap">Greater or equal to (Miles):</label>
                <input
                  className="h-9 w-[70px] rounded-[12px] border border-[#e5e7eb] px-2 text-[14px] text-[#111827] focus:border-[#FD7657] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20"
                  type="number"
                  name={`deliveryPolicies[${index}].startMile`}
                  id={`deliveryPolicies[${index}].startMile`}
                  value={formik.values.deliveryPolicies[index]?.startMile}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </div>
              <div className="flex items-center gap-x-2">
                <label className="text-[14px] font-medium text-[#4b5563] whitespace-nowrap">and less than (Miles):</label>
                <input
                  className="h-9 w-[70px] rounded-[12px] border border-[#e5e7eb] px-2 text-[14px] text-[#111827] focus:border-[#FD7657] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20"
                  type="number"
                  name={`deliveryPolicies[${index}].endMile`}
                  id={`deliveryPolicies[${index}].endMile`}
                  value={formik.values.deliveryPolicies[index]?.endMile || ""}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </div>
              <div className="flex items-center gap-x-2">
                <label className="text-[14px] font-medium text-[#4b5563] whitespace-nowrap">Delivery Charges ($):</label>
                <input
                  className="h-9 w-[70px] rounded-[12px] border border-[#e5e7eb] px-2 text-[14px] text-[#111827] focus:border-[#FD7657] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20"
                  type="number"
                  name={`deliveryPolicies[${index}].deliveryChargeCents`}
                  id={`deliveryPolicies[${index}].deliveryChargeCents`}
                  value={formik.values.deliveryPolicies[index]?.deliveryChargeCents}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue(`deliveryPolicies[${index}].deliveryChargeCents`, e.target.value.toString())}
                />
              </div>
              {index > 0 && (
                <button type="button" className="p-1 text-[#6b7280] hover:text-[#111827] focus:outline-none" onClick={() => handleRemoveDeliveryPolicy(index)} aria-label="Remove">
                  <img className="h-3 w-3" src={CrossIcon} alt="" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-4">
          {loading ? (
            <BtnLoader />
          ) : (
            <button
              type="button"
              disabled={isDisabled}
              className={`px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={updateRestaurantSetting}
            >
              Update
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryPolicy;